import { Member } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useCreateCustomGroup } from 'chat/conversations/group/context'
import { filterMembers } from 'chat/members/recipients/util'
import { SelectMembersView } from 'chat/members/views/select'
import { useCompaniesContext } from 'company/context'
import { StarRecipientsScreenProps } from 'home/stack/types'
import { unionBy } from 'lodash'
import { useUserList } from 'management_team/queries/list'
import { mapObserverToMember } from 'management_team/util'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { errorAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUser } from 'users/queries/detail'

export function StarRecipientsScreen({
  navigation,
  route,
}: StarRecipientsScreenProps) {
  const { type } = route?.params
  const isAward = type === 'award' ? true : false

  const [searchTerm, setSearchTerm] = useState<string>('')
  const { activeCompany } = useCompaniesContext()
  const { data: user } = useUser(undefined, activeCompany?.id)

  const { theme } = useTheme()

  const companyManagersQuery = useUserList(activeCompany?.id, 'manager')
  const companyHelpersQuery = useUserList(activeCompany?.id, 'helper')
  const observerQuery = useUserList(activeCompany?.id, 'observer')

  const { members, setMembers } = useCreateCustomGroup()

  const recipients = useMemo(
    () =>
      unionBy(
        companyManagersQuery.data?.map(mapObserverToMember) || [],
        companyHelpersQuery.data?.map(mapObserverToMember) || [],
        'id'
      ).filter(
        member =>
          !member.roles?.includes('observer') &&
          !member.roles?.includes('admin')
      ),
    [companyManagersQuery.data, companyHelpersQuery.data]
  )

  const observerRecipients = useMemo(
    () => observerQuery.data?.map(mapObserverToMember) || [],
    [observerQuery.data]
  )

  const [filteredRecipients, setFilteredRecipients] = useState<Member[]>(
    isAward ? recipients : observerRecipients
  )

  const onPressNext = useCallback(async () => {
    if (Object.keys(members).length > (user?.goldStars || 0)) {
      await errorAlert(
        new Error(
          t('insufficientGoldStarsDescription', {
            goldStars: user?.goldStars ?? 0,
          })
        ),
        t('insufficientGoldStars')
      )
    } else {
      navigation.navigate('award_transfer_gold_star', { type: type })
    }
  }, [members, navigation, type, user?.goldStars])

  useLayoutEffect(() => {
    showHeaderTitle(navigation, t('pickMembers'))
  }, [navigation])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      theme,
      title: t('next'),
      onPress: onPressNext,
      disabled: Object.keys(members).length === 0,
    })
  }, [
    filteredRecipients.length,
    members,
    navigation,
    onPressNext,
    theme,
    user?.goldStars,
  ])

  useEffect(() => {
    const filteredMembers = filterMembers(
      isAward ? recipients : observerRecipients,
      searchTerm
    )
    setFilteredRecipients(filteredMembers)
  }, [isAward, observerRecipients, recipients, searchTerm])

  useEffect(() => {
    setMembers({})
  }, [setMembers])

  return (
    <StatusBoundary
      isLoading={
        companyManagersQuery.isLoading ||
        companyHelpersQuery.isLoading ||
        observerQuery.isLoading
      }
      error={
        companyManagersQuery.error ||
        companyHelpersQuery.error ||
        observerQuery.error
      }
    >
      <SelectMembersView
        isSingleSelectList={type === 'transfer' ? true : false}
        recipients={filteredRecipients}
        selectedMembers={members}
        disabledMemberIds={user ? [user.id] : []}
        onMembersSelected={setMembers}
        searchText={searchTerm}
        setSearchText={setSearchTerm}
        disableAliasSelection={true}
      />
    </StatusBoundary>
  )
}
